/**
 * JWT decode utility - client-side only (no verification).
 * Used for reading exp/iat to schedule token refresh and expiry warnings.
 */

export interface DecodedJwtPayload {
  exp: number; // Expiry timestamp (seconds since epoch)
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

/**
 * Decode JWT payload without verification (client-side).
 * Returns null if token is invalid or malformed.
 */
export function decodeToken(accessToken: string): DecodedJwtPayload | null {
  if (!accessToken || typeof accessToken !== "string") {
    return null;
  }

  try {
    const parts = accessToken.split(".");
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    if (!payload) return null;

    // Base64url decode
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(base64);
    const decoded = JSON.parse(json) as Record<string, unknown>;

    if (typeof decoded.exp !== "number") {
      return null;
    }

    return decoded as DecodedJwtPayload;
  } catch {
    return null;
  }
}

/**
 * Get access token expiry timestamp in milliseconds.
 * Falls back to accessExpiresInSec from login response if JWT decode fails.
 */
export function getAccessTokenExpiryMs(
  accessToken: string,
  accessExpiresInSec?: number
): number | null {
  const decoded = decodeToken(accessToken);
  if (decoded?.exp) {
    // exp is in seconds; convert to ms
    return decoded.exp * 1000;
  }
  if (accessExpiresInSec && accessExpiresInSec > 0) {
    // Fallback: assume token was just issued
    return Date.now() + accessExpiresInSec * 1000;
  }
  return null;
}
